import React, { useEffect } from 'react';
import driftScript from './drift-script';

import moment, { Moment } from 'moment-timezone';
import { WeekDay } from '../../../types/types';

interface DriftSchedule {
  interactionId: number;
  schedule?: {
    days: WeekDay | WeekDay[];
    startTime: Moment;
    endTime: Moment;
    timezone?: string;
  };
}

interface Props {
  delay?: number;
  interaction: number | DriftSchedule[];
  goToConversation?: boolean;
  callback?: () => void;
}

const DriftBot = ({
  delay = 0,
  interaction,
  goToConversation = false,
  callback = () => {
    return;
  }
}: Props) => {
  const delayMilliseconds = delay * 1000;
  let script: HTMLScriptElement;
  let timeoutId: NodeJS.Timer;

  const normalizeSchedule = (item: DriftSchedule) => {
    const { schedule } = item;
    if (!schedule) {
      return item;
    }
    const { startTime, endTime, timezone = 'America/Denver' } = schedule;
    // Omitted units default to 0 or the current date, month, and year. https://momentjs.com/docs/#/parsing/object/
    schedule.startTime = moment.tz(
      {
        hour: startTime.hour(),
        minute: startTime.minute()
      },
      timezone
    );
    schedule.endTime = moment.tz(
      {
        hour: endTime.hour(),
        minute: endTime.minute()
      },
      timezone
    );
    return { ...item, schedule, timezone };
  };

  const getInteractionId = () => {
    // If passed in just an interactionId and no schedule, return the id
    if (typeof interaction === 'number') {
      return interaction;
    }

    const scheduledInteraction = interaction
      .map(item => normalizeSchedule(item))
      .filter(item => {
        if (item.schedule) {
          const currentTime = moment.tz(moment(), item.schedule.timezone!);
          const today = currentTime.weekday();
          let isTodayValid = false;
          if (Array.isArray(item.schedule.days)) {
            isTodayValid = item.schedule.days.indexOf(today) !== -1;
          } else {
            isTodayValid = today === item.schedule.days;
          }
          return (
            currentTime.isBetween(
              item.schedule.startTime,
              item.schedule.endTime
            ) && isTodayValid
          );
        }
        // If no schedule set then return interaction as a fallback
        return true;
      })
      .sort((firstItem, secondItem) => {
        // Sort interactions with a schedule first and those without a schedule last as a fallback
        if (firstItem.schedule && !secondItem.schedule) {
          return -1;
        } else if (!firstItem.schedule && secondItem.schedule) {
          return 1;
        }
        return 0;
      });

    if (scheduledInteraction.length) {
      return scheduledInteraction[0].interactionId;
    }

    return undefined;
  };

  useEffect(() => {
    setTimeout(() => {
      if (!('drift' in window)) {
        script = document.createElement('script');
        script.innerHTML = driftScript;
        script.async = true;
        document.body.appendChild(script);
      }
      timeoutId = setInterval(() => {
        if ('drift' in window) {
          clearInterval(timeoutId);
          window.drift.on('ready', (api: any) => {
            callback();
            const interactionId = getInteractionId();
            if (interactionId) {
              api.startInteraction({ interactionId, goToConversation });
            } else {
              // tslint:disable-next-line:no-console
              console.log('Drift: No valid interaction ID found for schedule.');
            }
          });
        }
      }, 100);
    }, delayMilliseconds);

    return () => {
      clearInterval(timeoutId);
    };
  }, []);

  return <div id="DriftBot" />;
};

export default DriftBot;
